import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaVolopHit } from '#/modules/search/algolia/types/SearchAlgoliaVolopHit';
import { useOrgVolopCountsByOrgId } from '#/modules/search/contexts/SearchContext';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { SearchVolopHitOrgLinkHolder } from './SearchVolopHitOrgLink.styled';

type Props = {
  hit: SearchAlgoliaVolopHit;
};

function useOrgLinkLabel(hit: SearchAlgoliaVolopHit) {
  const { i18n } = useI18n();
  const orgVolopCountsByOrgId = useOrgVolopCountsByOrgId();

  if (!hit.orgID) return null;

  const totalCount = orgVolopCountsByOrgId?.[hit.orgID];
  const remainingCount = totalCount !== undefined ? totalCount - 1 : undefined;

  if (remainingCount === undefined) {
    return {
      label: i18n('View all opportunities from this organization'),
      remainingCount,
    };
  }

  if (remainingCount <= 0) return null;

  const label =
    remainingCount === 1
      ? i18n('View 1 more opportunity from this organization')
      : i18n('View %(count)s more opportunities from this organization', {
          count: remainingCount,
        });

  return { label, remainingCount };
}

export function SearchVolopHitOrgLink({ hit }: Props) {
  const { locale } = useI18n();
  const { appConfig } = useAppConfig();

  const orgUrl = hit.orgUrl?.[locale] ?? null;
  const orgLinkLabel = useOrgLinkLabel(hit);

  if (hit.isPostedAnonymously || !orgUrl || !orgLinkLabel) {
    return null;
  }

  const url = new URL(orgUrl, appConfig.idealist.appUrl);
  url.searchParams.set('tab', 'VOLOP');
  url.hash = '#opportunities';
  const urlString = `${url.pathname}${url.search}${url.hash}`;

  return (
    <SearchVolopHitOrgLinkHolder>
      <StyledVersionedRouterLink
        qaId="search-volop-org-link"
        to={urlString}
        onClick={() => {
          trackClicked('Org Link From Search', {
            org_id: hit.orgID,
            org_name: hit.orgName,
            remaining_count: orgLinkLabel.remainingCount,
          });
        }}
      >
        {orgLinkLabel.label}
      </StyledVersionedRouterLink>
    </SearchVolopHitOrgLinkHolder>
  );
}
